import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  InputNumber,
  Space,
  Alert,
  Tag,
  Select,
} from 'antd';
import dayjs from 'dayjs';
import { Helmet } from 'react-helmet';
import { SearchOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Option } = Select;

const PlanManagement = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const searchInput = useRef(null);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');

  const planTypes = [
    '3rd Party Services',
    'Partner',
    'Employee',
    'Business'
  ];

  const clearMessages = () => {
    setSuccessMsg('');
    setErrorMsg('');
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
          autoFocus
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) => {
      if (searchedColumn === dataIndex && searchText) {
        const regex = new RegExp(searchText, 'gi');
        const parts = String(text).split(regex);
        return (
          <>
            {parts.map((part, index) =>
              index > 0 ? (
                <span key={index} style={{ backgroundColor: '#ffc069' }}>
                  {searchText}
                </span>
              ) : (
                part
              )
            )}
          </>
        );
      }
      return text;
    },
  });

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/plans');
      setPlans(data.data);
    } catch (err) {
      setErrorMsg('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  const handleAdd = () => {
    clearMessages();
    form.resetFields();
    setEditingId(null);
    setVisible(true);
  };

  const handleEdit = (record) => {
    clearMessages();
    form.setFieldsValue({
      ...record,
      expiration_date: record.expiration_date ? dayjs(record.expiration_date) : null,
    });
    setEditingId(record.id);
    setVisible(true);
  };

  const handleDelete = (id) => {
    clearMessages();
    Modal.confirm({
      title: 'Delete this plan?',
      onOk: async () => {
        try {
          await api.delete(`/plans/${id}`);
          fetchPlans();
          setSuccessMsg('Plan deleted successfully');
        } catch {
          setErrorMsg('Failed to delete plan');
        }
      },
    });
  };

  const handleSubmit = async () => {
    try {
      clearMessages();
      const values = await form.validateFields();
      const payload = {
        ...values,
        expiration_date: values.expiration_date ? values.expiration_date.format('YYYY-MM-DD') : null,
      };
      
      if (editingId) {
        await api.put(`/plans/${editingId}`, payload);
        setSuccessMsg('Plan updated successfully');
      } else {
        await api.post('/plans', payload);
        setSuccessMsg('Plan created successfully');
      }
      
      setVisible(false);
      fetchPlans();
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Failed to save plan');
    }
  };

  const columns = [
    {
      title: 'S.No.',
      key: 'sno',
      width: 80,
      render: (_text, _record, index) => index + 1,
    },
    { 
      title: 'Plan Name', 
      dataIndex: 'plan_name', 
      key: 'plan_name',
      ...getColumnSearchProps('plan_name'),
    },
    { 
      title: 'Plan Type', 
      dataIndex: 'plan_type', 
      key: 'plan_type',
      ...getColumnSearchProps('plan_type'),
    },
    { 
      title: 'Validity (Days)', 
      dataIndex: 'validity_days', 
      key: 'validity_days',
      ...getColumnSearchProps('validity_days'),
    },
    {
      title: 'Expiration',
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : 'N/A',
      ...getColumnSearchProps('expiration_date'),
    },
    {
      title: 'Status',
      dataIndex: 'expiration_date',
      key: 'status',
      render: (date) => {
        if (!date) return <Tag color="blue">Active</Tag>;
        const expired = dayjs(date).isBefore(dayjs());
        return <Tag color={expired ? 'red' : 'green'}>{expired ? 'Expired' : 'Active'}</Tag>;
      },
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Expired', value: 'expired' },
      ],
      onFilter: (value, record) => {
        if (!record.expiration_date) return value === 'active';
        const expired = dayjs(record.expiration_date).isBefore(dayjs());
        return value === 'active' ? !expired : expired;
      },
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (value) => `₹${parseFloat(value).toFixed(2)}`,
      ...getColumnSearchProps('price'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => handleEdit(record)}>Edit</Button>
          <Button size="small" danger onClick={() => handleDelete(record.id)}>Delete</Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Plan Management</title>
      </Helmet>
      
      <div style={{ padding: '20px' }}>
        <h2>Plan Management</h2>

        <Space style={{ marginBottom: 16 }}>
          <Button type="primary" onClick={handleAdd}>Add Plan</Button>
        </Space>

        {successMsg && (
          <Alert
            message={successMsg}
            type="success"
            showIcon
            closable
            onClose={() => setSuccessMsg('')}
            style={{ marginBottom: 12 }}
          />
        )}

        {errorMsg && (
          <Alert
            message={errorMsg}
            type="error"
            showIcon
            closable
            onClose={() => setErrorMsg('')}
            style={{ marginBottom: 12 }}
          />
        )}

        <Table
          rowKey="id"
          columns={columns}
          dataSource={plans}
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
        />

        <Modal
          visible={visible}
          title={editingId ? 'Edit Plan' : 'Add Plan'}
          onCancel={() => {
            setVisible(false);
            setEditingId(null);
          }}
          onOk={handleSubmit}
          destroyOnClose
          width={600}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              label="Plan Name"
              name="plan_name"
              rules={[{ required: true, message: 'Please enter plan name' }]}
            >
              <Input placeholder="Enter plan name" />
            </Form.Item>

            <Form.Item
              label="Plan Type"
              name="plan_type"
              rules={[{ required: true, message: 'Please select plan type' }]}
            >
              <Select placeholder="Select plan type">
                {planTypes.map(type => (
                  <Option key={type} value={type}>{type}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Validity (Days)"
              name="validity_days"
              rules={[{ required: true, message: 'Please enter validity days' }]}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label="Expiration Date"
              name="expiration_date"
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label="Price (INR)"
              name="price"
              rules={[{ required: true, message: 'Please enter price' }]}
            >
              <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label="Description"
              name="content"
            >
              <Input.TextArea rows={4} placeholder="Enter plan description" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </>
  );
};

export default PlanManagement;